import { Theme } from "@screencloud/screencloud-ui-components";
import styled from "styled-components";

export const Column = styled.div<{ $variant: "live" | "changes" }>`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  /* Scrollable without a visible scrollbar. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  flex-direction: column;
  padding-bottom: var(--spacing-xl, 24px);
  /* Figma distinguishes the two surfaces by background only: the live column
     uses gray-50 and the draft column a slightly darker neutral with a soft
     inset shadow on its left edge (no hard divider between the columns). The
     #efeff1 draft neutral sits between gray-50 (#f6f6f7) and gray-100
     (#e2e3e5) and has no matching theme token. */
  background: ${({ $variant }) =>
    $variant === "live" ? "var(--color-gray-50, #f6f6f7)" : "#efeff1"};
  box-shadow: ${({ $variant }) =>
    $variant === "changes" ? "inset 4px 0 16px 0 rgba(0, 0, 0, 0.03)" : "none"};
`;

export const Header = styled.div<{ $variant: "live" | "changes" }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xxs, 4px);
  padding: var(--spacing-lg, 16px) var(--spacing-xl, 24px);
  /* Pin the column title while the rows below scroll. Opaque background
     matching its own column (live = gray-50, draft = the darker #efeff1) so
     rows don't show through, and carry the draft column's left inset shadow
     through the sticky header so the edge treatment stays continuous. */
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ $variant }) =>
    $variant === "live" ? "var(--color-gray-50, #f6f6f7)" : "#efeff1"};
  box-shadow: ${({ $variant }) =>
    $variant === "changes" ? "inset 4px 0 16px 0 rgba(0, 0, 0, 0.03)" : "none"};
`;

export const Dot = styled.span<{ $variant: "live" | "changes" }>`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin-top: 7px;
  border-radius: 50%;
  background: ${({ $variant }) =>
    $variant === "live"
      ? "var(--color-green-700, #1b7a3a)"
      : "var(--color-yellow-500, #fecf00)"};
`;

export const HeaderTextBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const HeaderTitle = styled.span<{ $variant: "live" | "changes" }>`
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  color: ${({ $variant }) =>
    $variant === "live"
      ? "var(--color-green-700, #1b7a3a)"
      : "var(--color-yellow-950, #463500)"};
`;

export const HeaderSubtitle = styled.span`
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-subtle, #7d7e84);
`;

export const HeaderAside = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  align-self: center;
  gap: var(--spacing-xxs, 4px);
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-subtle, #7d7e84);
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  /* As a flex child of the scrollable column, the list must keep its natural
     height — shrinking would clip rows (overflow: hidden below is only for
     the rounded corners) and leave the column with nothing to scroll. */
  flex-shrink: 0;
  margin: 0 var(--spacing-xl, 24px);
  background: var(--color-background, #fff);
  border: 1px solid var(--color-gray-100, #e2e3e5);
  border-radius: var(--radius-md, 8px);
  ${Theme.default.inputShadow};
  /* No overflow clipping: the find button straddles the card's right border.
     Corner rounding is handled by the first/last rows instead. */
`;

export const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: var(--spacing-sm, 8px);
  padding: var(--spacing-lg, 16px) var(--spacing-xl, 24px) 0;
  /* With no list card (empty column), start where the card would have so the
     header padding is not doubled (GRWBKK-2025). */
  ${Header} + & {
    padding-top: 0;
  }
`;

export const SettingsTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-subtle, #7d7e84);
`;

export const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 8px);
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-gray-950, #19191b);
`;
